import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  eatsQueries,
  type MerchantModerationState,
  type Restaurant,
} from '@noor/eats-api';
import { toBranches } from '../lib/map-branch';
import type { Branch, BranchModeration } from './schema';

/** What the branches screen needs: the rows and the query flags. */
export interface BranchesResult {
  branches: Branch[];
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
}

/**
 * The merchant's branches, mapped to the list's view model.
 *
 * The listing is already narrowed to the caller's merchant by the JWT claim and
 * always includes hidden branches, so no id or filter is passed.
 *
 * It deliberately does NOT resolve the owner heading. `v1Merchant` carries only
 * `{id, name, status}` — no logo — so the brand identity comes from the OLD
 * backend's `GET /vendors/me`, which is a different entity
 * (`entities/legacy-profile`) and one FSD forbids this slice from importing.
 * The PAGES compose the two, which is also what keeps this heading identical to
 * the profile screen's identity card instead of drifting from it.
 */
/** Server-side narrowing of the branch list, straight from the screen's select. */
export interface BranchFilters {
  /** Moderation state; absent lists every branch. */
  status?: BranchModeration | undefined;
  /** When true, fetch published restaurants visible to clients (Режим витрина). */
  showcase?: boolean | undefined;
}

export function useBranches(
  filters?: BranchFilters,
  options?: { enabled?: boolean },
): BranchesResult {
  const isShowcase = Boolean(filters?.showcase);
  const moderationState = toMerchantModerationState(filters?.status);
  // Showcase (Витрина) mode reads each section from its own `*/published`
  // endpoint. The page disables the sections that are not on screen, so only
  // the active section's request fires.
  const gate = options?.enabled ?? true;

  const normalQuery = useQuery({
    ...eatsQueries.restaurantList(
      moderationState ? { state: moderationState } : undefined,
    ),
    enabled: !isShowcase && gate,
  });

  const showcaseQuery = useQuery({
    ...eatsQueries.publishedRestaurants(),
    enabled: isShowcase && gate,
  });

  const query = isShowcase ? showcaseQuery : normalQuery;

  const restaurants = useMemo<Restaurant[]>(() => query.data?.list ?? [], [query.data]);
  const branches = useMemo(() => toBranches(restaurants), [restaurants]);

  return {
    branches,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: () => void query.refetch(),
  };
}

function toMerchantModerationState(
  status: BranchModeration | undefined,
): MerchantModerationState | undefined {
  if (status === 'unspecified') return 'MERCHANT_MODERATION_STATE_UNSPECIFIED';
  if (status === 'draft') return 'MERCHANT_MODERATION_STATE_DRAFT';
  if (status === 'pending') return 'MERCHANT_MODERATION_STATE_ON_MODERATION';
  if (status === 'approved') return 'MERCHANT_MODERATION_STATE_APPROVED';
  if (status === 'rejected') return 'MERCHANT_MODERATION_STATE_REJECTED';
  return undefined;
}
